import React, { useEffect, useState } from 'react';

import { useDarkMode } from '@/hooks/useDarkMode';
import constants from '@/theme/exportedVariables.module.scss';
import { Container } from '@nivo/core';
import { RadarDataProps, RadarSvgProps, ResponsiveRadar } from '@nivo/radar';
import { mergeDeepLeft } from 'ramda';
import { useTimeoutFn } from 'react-use';

import styles from './RadarChart.module.scss';

type RadarChartDataItem = Record<string, unknown>;

export type RadarChartData = RadarChartDataItem[];

export type RadarChartProps = {
	data: RadarChartData;
	keys: string[];
	indexBy: string;
	maxValue?: number;
	wrapperClassName?: string;
	animationDelay?: number;
	colors?: string[];
} & Pick<
	RadarSvgProps<RadarChartDataItem>,
	| 'margin'
	| 'curve'
	| 'isInteractive'
	| 'colors'
	| 'defs'
	| 'fill'
	| 'theme'
	| 'animate'
	| 'fillOpacity'
	| 'enableDots'
	| 'dotSize'
	| 'borderWidth'
	| 'sliceTooltip'
	| 'gridLabel'
	| 'layers'
	| 'dotColor'
	| 'dotBorderWidth'
	| 'dotBorderColor'
>;

export const RadarChart: React.FC<RadarChartProps> = ({
	data,
	keys,
	indexBy,
	wrapperClassName,
	animationDelay,
	theme,
	...otherRadarChartProps
}) => {
	const { isDarkModeOn } = useDarkMode();

	return (
		<div className={wrapperClassName}>
			<ResponsiveRadar
				data={data}
				keys={keys}
				indexBy={indexBy}
				borderColor={{ from: 'color' }}
				gridLabelOffset={16}
				dotColor={{ theme: 'background' }}
				blendMode="normal"
				fillOpacity={0.5}
				enableDots={false}
				theme={mergeDeepLeft(theme || {}, {
					text: {
						fill: isDarkModeOn ? '#ffffff' : '#000000',
					},
					grid: {
						line: {
							stroke: isDarkModeOn ? '#313a4e' : '#cfdcf1',
							strokeDasharray: 0,
						},
					},
				})}
				curve={'catmullRomClosed'}
				colors={isDarkModeOn ? ['#56a0ff'] : ['#0040ff']}
				margin={{
					bottom: 50,
					right: -30,
					top: 25,
				}}
				animate={false}
				isInteractive={false}
				{...otherRadarChartProps}
			/>
		</div>
	);
};
